import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Armchair, Play, Sun, Moon, Footprints, ListOrdered, Compass, X, Sparkles, ChevronRight } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
export type DesignMode = 'exterior' | 'interior';
export type NavigationMode = 'walk' | 'tour' | 'step-by-step';
interface DesignModeSelectorAltProps {
  activeMode: DesignMode;
  onSelectMode: (mode: DesignMode) => void;
  navigationMode: NavigationMode;
  onNavigationModeChange: (mode: NavigationMode) => void;
  isAssistantActive?: boolean;
  onToggleAssistant?: () => void;
  onNavigatePanelOpen?: () => void;
}
export function DesignModeSelectorAlt({
  activeMode,
  onSelectMode,
  navigationMode,
  onNavigationModeChange,
  isAssistantActive = false,
  onToggleAssistant,
  onNavigatePanelOpen
}: DesignModeSelectorAltProps) {
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const [isNavPanelOpen, setIsNavPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Close panel on outside click
  useEffect(() => {
    if (!isNavPanelOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the panel or the nav button
      if (panelRef.current?.contains(target)) return;
      if (target.closest('[data-nav-trigger]')) return;
      setIsNavPanelOpen(false);
    };
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [isNavPanelOpen]);
  const designModes: {
    id: DesignMode;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'exterior',
    label: 'Exterior',
    icon: Building2
  }, {
    id: 'interior',
    label: 'Interior',
    icon: Armchair
  }];
  const navModes: {
    id: NavigationMode;
    label: string;
    description: string;
    icon: React.ElementType;
  }[] = [{
    id: 'walk',
    label: 'Walk',
    description: 'Free-roam exploration',
    icon: Footprints
  }, {
    id: 'tour',
    label: 'Guided Tour',
    description: 'Auto-guided walkthrough',
    icon: Play
  }, {
    id: 'step-by-step',
    label: 'Step-by-Step',
    description: 'Item-by-item customization',
    icon: ListOrdered
  }];
  const handleNavModeSelect = (mode: NavigationMode) => {
    onNavigationModeChange(mode);
    setIsNavPanelOpen(false);
  };
  const handleAssistantClick = () => {
    onToggleAssistant?.();
    setIsNavPanelOpen(false);
  };
  const handleAssistantClickFromRail = () => {
    onToggleAssistant?.();
  };
  return <>
      {/* Left Rail */}
      <div className={`fixed left-0 top-0 bottom-0 w-[100px] backdrop-blur-xl z-50 flex flex-col items-center py-8 shadow-2xl transition-all duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40 shadow-xl'}`} data-id="element-577">
        {/* Design Modes */}
        <div className="flex flex-col gap-6 w-full" data-id="element-578">
          {designModes.map(mode => {
          const isActive = activeMode === mode.id;
          const Icon = mode.icon;
          return <button key={mode.id} onClick={() => onSelectMode(mode.id)} className="relative group w-full flex flex-col items-center justify-center py-3 gap-1.5 transition-all outline-none" aria-label={`Switch to ${mode.label} mode`} data-id="element-579">
                {isActive && <motion.div layoutId="activeDesignModeBorderAlt" className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.3
            }} data-id="element-580" />}
                <div className={`p-3 rounded-xl transition-all duration-300 ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-600/15 text-teal-600' : isDark ? 'text-slate-400 group-hover:text-white group-hover:bg-white/5' : 'text-gray-500 group-hover:text-gray-900 group-hover:bg-white/50'}`} data-id="element-581">
                  <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-582" />
                </div>
                <span className={`text-[10px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-2 ${isActive ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-slate-500 group-hover:text-slate-300' : 'text-gray-500 group-hover:text-gray-700'}`} data-id="element-583">
                  {mode.label}
                </span>
              </button>;
        })}
        </div>

        <div className="flex-1" data-id="element-584" />

        {/* Navigation Modes Section */}
        <div className="flex flex-col gap-4 w-full mb-8" data-id="element-585">
          <div className={`h-px w-12 mx-auto ${isDark ? 'bg-white/10' : 'bg-black/10'}`} data-id="element-586" />
          <span className={`text-[8px] font-bold uppercase tracking-[0.2em] text-center ${isDark ? 'text-white/25' : 'text-gray-400'}`} data-id="element-587">
            Modes
          </span>

          {/* AI Assistant Button — Special Treatment */}
          <button onClick={handleAssistantClickFromRail} className="relative group w-full flex flex-col items-center justify-center py-2 gap-1 transition-all outline-none" aria-label="Toggle AI Assistant" data-id="element-588">
            {/* Active indicator */}
            {isAssistantActive && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} data-id="element-589" />}

            {/* Icon Container */}
            <div className="relative" data-id="element-590">
              <div className={`
                  p-2.5 rounded-lg transition-all duration-300 relative overflow-hidden
                  ${isAssistantActive ? isDark ? 'bg-gradient-to-br from-cyan-500/30 to-teal-500/20 text-cyan-300 shadow-[0_0_16px_rgba(6,182,212,0.3)]' : 'bg-gradient-to-br from-teal-500/20 to-cyan-500/15 text-teal-600 shadow-[0_0_16px_rgba(13,148,136,0.2)]' : isDark ? 'text-slate-500 group-hover:text-white group-hover:bg-white/5' : 'text-gray-400 group-hover:text-gray-900 group-hover:bg-black/5'}
                `} data-id="element-591">
                {/* Subtle shimmer effect — only when inactive to hint at specialness */}
                {!isAssistantActive && <div className="absolute inset-0 overflow-hidden rounded-lg" data-id="element-592">
                    <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 5,
                  ease: 'linear'
                }} className={`absolute inset-y-0 w-1/2 ${isDark ? 'bg-gradient-to-r from-transparent via-white/[0.06] to-transparent' : 'bg-gradient-to-r from-transparent via-white/40 to-transparent'}`} data-id="element-593" />
                  </div>}
                <Sparkles className={`w-5 h-5 relative z-10 ${isAssistantActive ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-594" />
              </div>

              {/* Pulsing dot when active */}
              {isAssistantActive && <motion.div animate={{
              scale: [1, 1.3, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-teal-500'} border border-slate-900`} data-id="element-595" />}

              {/* NEW badge — small dot indicator (only when inactive) */}
              {!isAssistantActive && <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}`} data-id="element-596" />}
            </div>

            {/* Label */}
            <span className={`
                text-[9px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-1
                ${isAssistantActive ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-slate-600 group-hover:text-slate-400' : 'text-gray-400 group-hover:text-gray-600'}
              `} data-id="element-597">
              AI
            </span>
          </button>

          {/* Thin separator between AI and Walk */}
          <div className={`h-px w-6 mx-auto ${isDark ? 'bg-white/5' : 'bg-black/5'}`} data-id="element-598" />

          {/* Walk Mode */}
          <button onClick={() => onNavigationModeChange('walk')} className="relative group w-full flex flex-col items-center justify-center py-2 gap-1 transition-all outline-none" aria-label="Switch to Walk mode" data-id="element-599">
            <div className={`
                p-2.5 rounded-lg transition-all duration-300
                ${navigationMode === 'walk' ? isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-900' : isDark ? 'text-slate-500 group-hover:text-white group-hover:bg-white/5' : 'text-gray-400 group-hover:text-gray-900 group-hover:bg-black/5'}
              `} data-id="element-600">
              <Footprints className={`w-5 h-5 ${navigationMode === 'walk' ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-601" />
            </div>
            <span className={`
                text-[9px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-1
                ${navigationMode === 'walk' ? isDark ? 'text-white' : 'text-gray-900' : isDark ? 'text-slate-600 group-hover:text-slate-400' : 'text-gray-400 group-hover:text-gray-600'}
              `} data-id="element-602">
              Walk
            </span>
          </button>

          {/* Tour Mode */}
          <button onClick={() => onNavigationModeChange('tour')} className="relative group w-full flex flex-col items-center justify-center py-2 gap-1 transition-all outline-none" aria-label="Switch to Tour mode" data-id="element-603">
            <div className={`
                p-2.5 rounded-lg transition-all duration-300
                ${navigationMode === 'tour' ? isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-900' : isDark ? 'text-slate-500 group-hover:text-white group-hover:bg-white/5' : 'text-gray-400 group-hover:text-gray-900 group-hover:bg-black/5'}
              `} data-id="element-604">
              <Play className={`w-5 h-5 ${navigationMode === 'tour' ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-605" />
            </div>
            <span className={`
                text-[9px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-1
                ${navigationMode === 'tour' ? isDark ? 'text-white' : 'text-gray-900' : isDark ? 'text-slate-600 group-hover:text-slate-400' : 'text-gray-400 group-hover:text-gray-600'}
              `} data-id="element-606">
              Tour
            </span>
          </button>

          {/* Step-by-Step Mode */}
          <button onClick={() => onNavigationModeChange('step-by-step')} className="relative group w-full flex flex-col items-center justify-center py-2 gap-1 transition-all outline-none" aria-label="Switch to Step-by-Step mode" data-id="element-607">
            <div className={`
                p-2.5 rounded-lg transition-all duration-300
                ${navigationMode === 'step-by-step' ? isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-900' : isDark ? 'text-slate-500 group-hover:text-white group-hover:bg-white/5' : 'text-gray-400 group-hover:text-gray-900 group-hover:bg-black/5'}
              `} data-id="element-608">
              <ListOrdered className={`w-5 h-5 ${navigationMode === 'step-by-step' ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-609" />
            </div>
            <span className={`
                text-[9px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-1 leading-tight
                ${navigationMode === 'step-by-step' ? isDark ? 'text-white' : 'text-gray-900' : isDark ? 'text-slate-600 group-hover:text-slate-400' : 'text-gray-400 group-hover:text-gray-600'}
              `} data-id="element-610">
              Step-by-Step
            </span>
          </button>
        </div>

        {/* Theme Toggle */}
        <div className="pb-6" data-id="element-611">
          <button onClick={toggleTheme} className={`p-3 rounded-xl transition-all duration-300 ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'}`} aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`} data-id="element-612">
            {isDark ? <Sun className="w-5 h-5" data-id="element-613" /> : <Moon className="w-5 h-5" data-id="element-614" />}
          </button>
        </div>
      </div>

      {/* Navigation Panel (slides out from rail) */}
      <AnimatePresence data-id="element-615">
        {isNavPanelOpen && <motion.div ref={panelRef} initial={{
        x: -240,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -240,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className={`fixed left-[100px] top-0 bottom-0 w-[240px] z-[52] backdrop-blur-xl shadow-2xl flex flex-col transition-colors duration-300 ${isDark ? 'bg-slate-900/95 border-r border-white/10' : 'bg-white/95 border-r border-white/40'}`} data-id="element-616">
            {/* Panel Header */}
            <div className={`p-5 pb-4 ${isDark ? 'border-b border-white/10' : 'border-b border-white/40'}`} data-id="element-617">
              <div className="flex items-center justify-between" data-id="element-618">
                <div data-id="element-619">
                  <h2 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-620">
                    Navigate
                  </h2>
                  <p className={`text-[11px] mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-id="element-621">
                    Choose how to explore
                  </p>
                </div>
                <button onClick={() => setIsNavPanelOpen(false)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-622">
                  <X className="w-4 h-4" data-id="element-623" />
                </button>
              </div>
            </div>

            {/* Navigation Modes */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto" data-id="element-624">
              {/* AI Assistant — Featured Card (above modes) */}
              <button onClick={handleAssistantClick} className={`w-full rounded-xl p-4 text-left transition-all group relative overflow-hidden ${isAssistantActive ? isDark ? 'bg-gradient-to-br from-cyan-500/25 to-teal-500/15 border border-cyan-500/30' : 'bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-300' : isDark ? 'bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 hover:border-cyan-500/30 hover:from-cyan-500/10 hover:to-teal-500/5' : 'bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-teal-300 hover:from-teal-50/50 hover:to-cyan-50/50'}`} data-id="element-625">
                {/* Subtle shimmer effect */}
                <div className="absolute inset-0 overflow-hidden rounded-xl" data-id="element-626">
                  <motion.div animate={{
                x: ['-100%', '200%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'linear'
              }} className={`absolute inset-y-0 w-1/3 ${isDark ? 'bg-gradient-to-r from-transparent via-white/[0.03] to-transparent' : 'bg-gradient-to-r from-transparent via-white/40 to-transparent'}`} data-id="element-627" />
                </div>

                <div className="relative" data-id="element-628">
                  {/* Top row: icon + NEW badge */}
                  <div className="flex items-center justify-between mb-3" data-id="element-629">
                    <div className={`p-2.5 rounded-lg ${isAssistantActive ? isDark ? 'bg-cyan-500/30 text-cyan-300' : 'bg-teal-500/20 text-teal-600' : isDark ? 'bg-white/10 text-cyan-400' : 'bg-teal-100 text-teal-600'}`} data-id="element-630">
                      <Sparkles className="w-5 h-5" data-id="element-631" />
                    </div>

                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${isDark ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30' : 'bg-teal-500 text-white'}`} data-id="element-632">
                      New
                    </span>
                  </div>

                  {/* Title + description */}
                  <div className="mb-2" data-id="element-633">
                    <h3 className={`text-sm font-semibold ${isAssistantActive ? isDark ? 'text-cyan-300' : 'text-teal-700' : isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-634">
                      AI Assistant
                    </h3>
                    <p className={`text-[11px] mt-0.5 leading-relaxed ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-id="element-635">
                      Voice-powered navigation & design guidance
                    </p>
                  </div>

                  {/* Action hint */}
                  <div className="flex items-center gap-1.5" data-id="element-636">
                    <span className={`text-[10px] font-medium ${isAssistantActive ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/50 group-hover:text-cyan-400' : 'text-gray-400 group-hover:text-teal-600'} transition-colors`} data-id="element-637">
                      {isAssistantActive ? 'Active — tap to end' : 'Tap to start'}
                    </span>
                    <ChevronRight className={`w-3 h-3 transition-all ${isAssistantActive ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/30 group-hover:text-cyan-400 group-hover:translate-x-0.5' : 'text-gray-300 group-hover:text-teal-600 group-hover:translate-x-0.5'}`} data-id="element-638" />
                  </div>

                  {/* Active pulse indicator */}
                  {isAssistantActive && <motion.div animate={{
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className={`absolute top-4 right-4 w-2 h-2 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-639" />}
                </div>
              </button>

              {/* Divider */}
              <div className={`h-px ${isDark ? 'bg-white/8' : 'bg-gray-200'}`} data-id="element-640" />

              {/* Modes section */}
              <div data-id="element-641">
                <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-642">
                  Modes
                </span>

                <div className="space-y-1 mt-2" data-id="element-643">
                  {navModes.map(mode => {
                const isActive = navigationMode === mode.id;
                const Icon = mode.icon;
                return <button key={mode.id} onClick={() => handleNavModeSelect(mode.id)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left group ${isActive ? isDark ? 'bg-white/10' : 'bg-gray-100' : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-id="element-644">
                        {/* Radio indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? isDark ? 'border-cyan-400' : 'border-teal-600' : isDark ? 'border-white/20 group-hover:border-white/40' : 'border-gray-300 group-hover:border-gray-400'}`} data-id="element-645">
                          {isActive && <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} className={`w-2.5 h-2.5 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-teal-600'}`} data-id="element-646" />}
                        </div>

                        {/* Icon */}
                        <div className={`p-2 rounded-lg transition-colors ${isActive ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-900' : isDark ? 'text-slate-400 group-hover:text-white' : 'text-gray-400 group-hover:text-gray-700'}`} data-id="element-647">
                          <Icon className="w-4 h-4" data-id="element-648" />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0" data-id="element-649">
                          <div className={`text-sm font-medium transition-colors ${isActive ? isDark ? 'text-white' : 'text-gray-900' : isDark ? 'text-slate-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'}`} data-id="element-650">
                            {mode.label}
                          </div>
                          <div className={`text-[10px] mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-651">
                            {mode.description}
                          </div>
                        </div>
                      </button>;
              })}
                </div>
              </div>
            </div>

            {/* Panel Footer */}
            <div className={`p-4 ${isDark ? 'border-t border-white/5' : 'border-t border-white/40'}`} data-id="element-652">
              <div className="flex items-center gap-2" data-id="element-653">
                <div className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}`} data-id="element-654" />
                <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-655">
                  Currently:{' '}
                  <span className={`font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-id="element-656">
                    {navigationMode === 'walk' ? 'Walk' : navigationMode === 'tour' ? 'Guided Tour' : 'Step-by-Step'}
                  </span>
                </span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}